import React from 'react';
import i18n from '../../../../../../i18n';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { DRILL_AXIS_TICK, DRILL_GRID, drillRankColor } from './colors';

export type BarDatum = {
  /** Axis label; may be shortened. */
  name: string;
  /** Untruncated label for the tooltip and the tick title. */
  fullName?: string;
  value: number;
  color?: string;
  /** Compare-period value for this bar. */
  compareValue?: number;
  /** Signed percent-change of value vs compareValue. */
  vsComparePct?: number | null;
};

/** Fits ~22-character labels (see OUTLET_LABEL_MAX) before recharts clips them. */
function yAxisWidth(data: BarDatum[], compact: boolean): number {
  const longest = data.reduce((n, d) => Math.max(n, d.name.length), 0);
  // Per-character estimate plus the tick gap, so "Afternoon" is not clipped.
  if (compact) return Math.min(150, Math.max(56, longest * 6.4 + 12));
  return Math.min(166, Math.max(72, longest * 7 + 12));
}

type TickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string; index?: number };
};

/** Category tick that carries the full label as an SVG title (hover). */
function CategoryTick({
  x = 0,
  y = 0,
  payload,
  data,
  tick,
}: TickProps & { data: BarDatum[]; tick: { fill: string; fontSize: number } }) {
  const label = String(payload?.value ?? '');
  const datum = payload?.index != null ? data[payload.index] : undefined;
  const full = datum?.fullName ?? label;
  return (
    <text
      x={x}
      y={y}
      dy="0.355em"
      textAnchor="end"
      fill={tick.fill}
      fontSize={tick.fontSize}
    >
      <title>{full}</title>
      {label}
    </text>
  );
}

export function DrillBarChart({
  data,
  fmtVal,
  height = 140,
  baseline,
  horizontal = false,
  compact = false,
  showValues = false,
  fill = false,
  maxHeight,
  compareLabel,
  labelFmt,
}: {
  data: BarDatum[];
  fmtVal: (v: number) => string;
  /** Value labels + tooltip (e.g. with "· n=15"); axis ticks keep `fmtVal`. */
  labelFmt?: (v: number) => string;
  height?: number;
  /** Optional baseline marker (rate/avg). */
  baseline?: number;
  horizontal?: boolean;
  /** Tighter ticks/margins for correlate mini-tiles. */
  compact?: boolean;
  showValues?: boolean;
  /** Grow to fill the tile body (pair height matching). */
  fill?: boolean;
  /** Cap for `fill`, so a tall neighbour tile does not spread a few bars thin. */
  maxHeight?: number;
  /** Legend label for the compare delta chip tooltip. */
  compareLabel?: string;
}) {
  if (!data.length) return null;
  const tick = compact
    ? { ...DRILL_AXIS_TICK, fontSize: 10 }
    : DRILL_AXIS_TICK;
  const catWidth = horizontal ? yAxisWidth(data, compact) : 40;
  const hasCompare = data.some(d => d.compareValue != null);
  const fmtLabel = labelFmt ?? fmtVal;
  // Room for the longest value label to the right of horizontal bars.
  const labelRoom =
    showValues && horizontal
      ? Math.ceil(
          data.reduce((n, d) => Math.max(n, fmtLabel(d.value).length), 0) *
            (compact ? 5.6 : 6.2),
        ) + 6
      : 0;

  const chart = (
    <BarChart
      data={data}
      layout={horizontal ? 'vertical' : 'horizontal'}
      margin={{
        top: compact ? 2 : 6,
        right: showValues
          ? Math.max(compact ? 40 : 36, labelRoom)
          : compact
            ? 4
            : 8,
        bottom: 0,
        left: 0,
      }}
      barCategoryGap={compact ? '18%' : fill ? '28%' : '16%'}
      barGap={compact ? 2 : 4}
    >
      <CartesianGrid
        stroke={DRILL_GRID}
        strokeDasharray="3 3"
        horizontal={!horizontal}
        vertical={false}
      />
      {horizontal ? (
        <>
          <XAxis
            type="number"
            tick={tick}
            tickLine={false}
            axisLine={false}
            tickFormatter={v => fmtVal(Number(v))}
            hide={compact}
          />
          <YAxis
            type="category"
            dataKey="name"
            tickLine={false}
            axisLine={false}
            width={catWidth}
            interval={0}
            tick={<CategoryTick data={data} tick={tick} />}
          />
        </>
      ) : (
        <>
          <XAxis
            dataKey="name"
            tick={tick}
            tickLine={false}
            axisLine={{ stroke: DRILL_GRID }}
            interval={0}
            angle={data.length > 6 ? -25 : 0}
            textAnchor={data.length > 6 ? 'end' : 'middle'}
            height={data.length > 6 ? 42 : 24}
          />
          <YAxis
            tick={tick}
            tickLine={false}
            axisLine={false}
            width={compact ? 32 : 40}
            tickFormatter={v => fmtVal(Number(v))}
          />
        </>
      )}
      <Tooltip
        itemSorter={item => (item?.dataKey === 'value' ? -1 : 1)}
        labelFormatter={(label: string, payload: Payload<number, string>[]) =>
          (payload?.[0]?.payload as BarDatum | undefined)?.fullName ?? String(label)
        }
        formatter={(raw, name, props) => {
          const value = Number(raw);
          const cur = Number(props?.payload?.value);
          const cmp = props?.payload?.compareValue as number | undefined;
          const pct = cmp != null && cmp !== 0 ? (cur / cmp - 1) * 100 : null;
          const delta =
            pct != null ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
          if (name === 'compareValue') {
            return [
              `${fmtVal(value)}${delta}`,
              compareLabel ?? i18n.t('performance:drillUi.compare'),
            ];
          }
          return [fmtLabel(value), i18n.t('performance:drillUi.thisPeriod')];
        }}
        contentStyle={{
          background: '#0E2C31',
          border: 'none',
          borderRadius: 8,
          fontSize: 12,
          color: '#fff',
        }}
        labelStyle={{ color: '#fff', fontWeight: 600 }}
        itemStyle={{ color: '#fff' }}
      />
      {baseline != null ? (
        <ReferenceLine
          {...(horizontal ? { x: baseline } : { y: baseline })}
          stroke="var(--ink-3)"
          strokeDasharray="4 3"
        />
      ) : null}
      <Bar
        dataKey="value"
        radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
        isAnimationActive={false}
        maxBarSize={horizontal ? (compact ? 10 : 14) : 28}
      >
        {data.map((d, i) => (
          <Cell key={`${d.name}-${i}`} fill={d.color ?? drillRankColor(i, data.length)} />
        ))}
        {showValues ? (
          <LabelList
            dataKey="value"
            position={horizontal ? 'right' : 'top'}
            formatter={(v: number) => fmtLabel(v)}
            style={{
              fill: 'var(--ink-2)',
              fontSize: compact ? 10 : 11,
            }}
          />
        ) : null}
      </Bar>
      {/* Compare after current: the dark current-period bar leads each pair. */}
      {hasCompare ? (
        <Bar
          dataKey="compareValue"
          radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
          isAnimationActive={false}
          maxBarSize={horizontal ? (compact ? 10 : 14) : 28}
          fill="var(--ink-3)"
          fillOpacity={0.28}
        >
          {showValues ? (
            <LabelList
              dataKey="compareValue"
              position={horizontal ? 'right' : 'top'}
              formatter={(v: number) => fmtVal(v)}
              style={{
                fill: 'var(--ink-3)',
                fontSize: compact ? 10 : 11,
              }}
            />
          ) : null}
        </Bar>
      ) : null}

    </BarChart>
  );

  return (
    <div
      className={`ddrill-chart${compact ? ' is-compact' : ''}${fill ? ' is-fill' : ''}`}
      style={fill ? (maxHeight ? { maxHeight } : undefined) : { height }}
      onClick={e => e.stopPropagation()}
    >
      <ResponsiveContainer width="100%" height="100%">
        {chart}
      </ResponsiveContainer>
    </div>
  );
}
